import React from 'react';
import { Button, Dialog, TextLink } from '@neo4j-ndl/react';
import { BookOpenIconOutline, BeakerIconOutline } from '@neo4j-ndl/react/icons';
import { Section, SectionTitle, SectionContent } from './ModalUtils';

export const version = '2.4.11-labs';

export const NeoAboutModal = ({ open, handleClose, getDebugState }) => {
  const downloadDebugFile = () => {
    const element = document.createElement('a');
    const state = getDebugState();
    state.version = version;
    const file = new Blob([JSON.stringify(state, null, 2)], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'visuiz-debug-state.json';
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  return (
    <>
      <Dialog onClose={handleClose} open={open} aria-labelledby='form-dialog-title' size='large'>
        <Dialog.Header>About Visuiz</Dialog.Header>
        <Dialog.Content>
          <div className='n-flex n-flex-col n-gap-token-4 n-divide-y n-divide-neutral-border-strong'>
            <Section>
              <SectionContent>
                Visuiz is a powerful and intuitive dashboard builder, designed for creating advanced visual analytics directly from Neo4j graph databases. With Visuiz, you can turn your Cypher queries into interactive dashboards within minutes — no complex setup or connectors required.
              </SectionContent>
            </Section>
            <Section>
              <SectionTitle>Core Features</SectionTitle>
              <SectionContent>
                <ul className='n-list-disc n-pl-token-8'>
                  <li>
                    <strong>Cypher Query Editor:</strong> Write and execute&nbsp;
                    <TextLink externalLink target='_blank' href='https://neo4j.com/developer/cypher/'>
                      Cypher
                    </TextLink>
                    &nbsp;queries directly within Visuiz.
                  </li>
                  <li>
                    <strong>Dynamic Visualizations:</strong> Instantly transform your data into rich visual formats such as tables, bar charts, graphs, and network visualizations.
                  </li>
                  <li>
                    <strong>Interactive Dashboards:</strong> Combine multiple reports into pages, apply custom styling, and add interactions between charts.
                  </li>
                  <li>
                    <strong>Share & Collaborate:</strong> Save your dashboards and securely share them with your team or clients.
                  </li>
                  <li>
                    <strong>Seamless Integration:</strong> Works natively with Neo4j—no extra connectors or data preprocessing needed.
                  </li>
                </ul>
              </SectionContent>
            </Section>
            <Section>
              <SectionTitle>Getting Started</SectionTitle>
              <SectionContent>
                When you open Visuiz for the first time, you’ll begin with an empty dashboard.
                <br />
                Click the{' '}
                <strong>
                  (<BookOpenIconOutline className='icon-base icon-inline text-r' /> Documentation)
                </strong>
                &nbsp;button to explore sample queries, visualizations, and best practices.
                <br />
                Build your first dashboard by running Cypher queries and adding visual components with just a few clicks.
              </SectionContent>
            </Section>
            <Section>
              <SectionTitle>Extending Visuiz</SectionTitle>
              <SectionContent>
                Visuiz is built with React and&nbsp;
                <TextLink target='_blank' href='https://github.com/adam-cowley/use-neo4j'>
                  use-neo4j
                </TextLink>
                , leveraging&nbsp;
                <TextLink target='_blank' href='https://github.com/recharts/recharts'>
                  Recharts
                </TextLink>
                &nbsp;for advanced visualizations and&nbsp;
                <TextLink target='_blank' href='https://www.openstreetmap.org/'>
                  OpenStreetMap
                </TextLink>
                &nbsp;for geospatial mapping. Developers can extend Visuiz by adding custom visual components or plugins — check out the developer guide in the&nbsp;
                <TextLink target='_blank' href='https://github.com/irusri/visuiz'>
                  project repository
                </TextLink>
                .
              </SectionContent>
            </Section>
            <Section>
              <SectionTitle>Contact</SectionTitle>
              <SectionContent>
                For suggestions, feature requests, or collaboration opportunities:
                <ul className='n-list-disc n-pl-token-8'>
                  <li>
                    Contact Irusri Group at&nbsp;
                    <TextLink href='mailto:info@irusri.com'>info@irusri.com</TextLink>
                  </li>
                  <li>
                    Visit&nbsp;
                    <TextLink target='_blank' href='https://www.irusri.se'>www.irusri.se</TextLink>
                  </li>
                  <li>
                    Join the community discussion on the&nbsp;
                    {/* <TextLink target='_blank' href='https://github.com/irusri/visuiz'> */}
                      Visuiz GitHub repository
                    {/* </TextLink> */}
                    &nbsp;or partner forums.
                  </li>
                </ul>
              </SectionContent>
            </Section>
          </div>
          <div className='n-flex n-flex-row n-justify-between n-mt-token-8'>
            <div>
              <Button onClick={downloadDebugFile} fill='outlined' color='neutral' size='small'>
                Debug Report
                <BeakerIconOutline className='btn-icon-sm-r' />
              </Button>
            </div>
            {/* <div>
              <i style={{ float: 'right', fontSize: '11px' }}>v{version}</i>
            </div> */}
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

export default NeoAboutModal;